import { useCallback } from "react";
import { SvgIcon } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ReactComponent as SearchIcon } from "../assets/icons/Search.svg";
import { ReactComponent as TitleIcon } from "../assets/icons/BuyerCompanies/TitleIcon.svg";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import BuyerCompaniesTable from "../components/BuyerCompaniesTable";
import ResultsDropdown from "../components/ResultsDropdown";
import PageSelector from "../components/PageSelector";
import styles from "../styles/BuyerCompanies.module.css";

const SORT_ASCENDING = "ascending";

/**
 * Title, search box, and create button for the buyer-companies page.
 * @param {Object} props
 * @param {Function} props.t - i18n translate function.
 * @param {Function} props.navigate - React Router navigate.
 * @param {string} props.searchQuery
 * @param {Function} props.onSearch
 * @param {Function} props.onCreate
 * @returns {JSX.Element}
 */
export const BuyerCompaniesHeader = ({
  t,
  navigate,
  searchQuery,
  onSearch,
  onCreate,
}) => (
  <div className={styles.header}>
    <div className={styles.languages}>
      <NotificationBell navigate={navigate} />
      <LanguageSelector />
    </div>

    <div className={styles.headerInfoContainer}>
      <div className={styles.headerInfo}>
        <SvgIcon sx={{ fontSize: "32px" }} viewBox="0 0 32 32">
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4eabe7" />
              <stop offset="100%" stopColor="#2f59df" />
            </linearGradient>
          </defs>
          <TitleIcon sx={{ fill: "url(#gradient1)" }} />
        </SvgIcon>
        <h1 className={styles.title}>{t("pages.buyerCompanies.header")}</h1>
      </div>
      <div className={styles.searchSection}>
        <div className={styles.searchBarContainer}>
          <div className={styles.searchBar}>
            <div style={{ position: "absolute", left: "1%" }}>
              <SvgIcon
                sx={{ fontSize: "16px", color: "#3B3B3D" }}
                component={SearchIcon}
              />
            </div>
            <input
              placeholder={t("pages.buyerCompanies.searchPlaceholder")}
              type="search"
              value={searchQuery}
              onChange={onSearch}
            />
          </div>
          <div className={styles.searchBarIcon}>
            <SvgIcon
              sx={{ height: "1.3rem", color: "#FCFCFC" }}
              component={SearchIcon}
            />
          </div>
        </div>
        <div className={styles.newBuyerCompanyButton} onClick={onCreate}>
          <SvgIcon component={AddIcon} fontSize="small" />
          {t("pages.buyerCompanies.createButton")}
        </div>
      </div>
    </div>
  </div>
);

/**
 * Empty state, or the buyer-companies table with page size and pager.
 * @param {Object} props
 * @param {Function} props.t
 * @param {Array} props.paginatedBuyerCompaniesList
 * @param {Array} props.filteredBuyerCompaniesList
 * @param {number} props.resultsLimit
 * @param {number} props.currentPage
 * @param {string} props.sortDirection
 * @param {Function} props.onSort
 * @param {Function} props.onResultsLimitChange
 * @param {Function} props.onPageChange
 * @returns {JSX.Element}
 */
export const BuyerCompaniesResults = ({
  t,
  paginatedBuyerCompaniesList,
  filteredBuyerCompaniesList,
  resultsLimit,
  currentPage,
  sortDirection,
  onSort,
  onResultsLimitChange,
  onPageChange,
}) => {
  /**
   * Sort icon for the company-name column. Rotated while the sort is ascending.
   * @returns {JSX.Element}
   */
  const renderArrow = useCallback(
    () => (
      <ArrowDownwardIcon
        className={`${styles.arrowIcon} ${
          sortDirection === SORT_ASCENDING ? styles.rotated : ""
        }`}
      />
    ),
    [sortDirection],
  );

  if (paginatedBuyerCompaniesList.length === 0) {
    return (
      <div className={styles.emptyState}>
        {t("pages.buyerCompanies.noResults")}
      </div>
    );
  }

  return (
    <>
      <BuyerCompaniesTable
        buyerCompanies={paginatedBuyerCompaniesList}
        onSort={onSort}
        renderArrow={renderArrow}
      />
      <div className={styles.footerContainer}>
        <div className={styles.footer}>
          <ResultsDropdown onResultsLimitChange={onResultsLimitChange} />
          <PageSelector
            currentPage={currentPage}
            totalPages={Math.ceil(
              filteredBuyerCompaniesList.length / resultsLimit,
            )}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </>
  );
};
